import { EXTERNAL_AUTH_ERROR, EXTERNAL_AUTH_TYPE } from '@pressreader/authentication-types';

import { logError } from 'shared/errors/error.handlers';

import { AuthContext, AuthorizeArgs } from './default.provider';
import LibraryBaseProvider from './library.base.provider';

interface SipAuthorizeContext extends AuthContext {
    authorized?: boolean;
    key?: string;
    authResult?: unknown;
}

interface SipAuthorizeArgs extends AuthorizeArgs {
    context?: SipAuthorizeContext;
}

class LibraryAuthProvider extends LibraryBaseProvider {
    override async authorize({ provider, authtype, context = {} }: SipAuthorizeArgs): Promise<unknown> {
        // libraries support only sign up
        if (authtype !== EXTERNAL_AUTH_TYPE.SIGNUP) {
            throw new Error(`${authtype} is not supported by ${provider} provider`);
        }

        // standalone window mode, out of pressreader site, used by native apps
        if (context.isWindowMode) {
            this.processAuthorization({ context });
            return;
        }

        if (!context.authorized) {
            throw new Error('pre-authorization expected');
        }

        // basic authorization is performed inside library sign-in dialog
        try {
            return await this.completeAuthorization({
                provider,
                authtype,
                key: context.key ?? '',
                authResult: context.authResult,
                context,
            });
        } catch (error) {
            // hide unexpected errors from client: reject without a value so callers
            // that branch on `!error` treat it as a silent dismissal.
            // `return Promise.reject()` (not `throw undefined`) avoids Sonar S3696.
            if (error && (error as { code?: string }).code === EXTERNAL_AUTH_ERROR.UNEXPECTED_ERROR) {
                logError(error);
                return Promise.reject();
            }
            throw error;
        }
    }
}

export { SipAuthorizeArgs, SipAuthorizeContext };
export default LibraryAuthProvider;
